import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../auth-config';
import { authService } from '../auth-config/auth-service';
import toast from 'react-hot-toast';
import { 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiMapPin, 
  FiBookOpen, 
  FiCalendar, 
  FiGlobe, 
  FiLock,
  FiSave,
  FiSettings
} from 'react-icons/fi';

const ProfilePage: React.FC = () => {
  const { user } = useAuthContext();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [saving, setSaving] = useState(false);

  // Initialize form fields with user attributes
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || user.attributes?.firstName || '');
      setLastName(user.lastName || user.attributes?.lastName || '');
      setPhoneNumber(user.attributes?.phone_number || user.attributes?.phoneNumber || '');
      setAddress(user.attributes?.address || '');
    }
  }, [user]);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('First Name and Last Name are required.');
      return;
    }

    // Clean and validate phone number format for AWS Cognito E.164 requirement
    let formattedPhone = phoneNumber.trim();
    if (formattedPhone) {
      formattedPhone = formattedPhone.replace(/[\s\-()]/g, '');
      if (!formattedPhone.startsWith('+')) {
        if (formattedPhone.length === 12 && formattedPhone.startsWith('91')) {
          formattedPhone = `+${formattedPhone}`;
        } else if (formattedPhone.length === 10) {
          formattedPhone = `+91${formattedPhone}`;
        } else {
          toast.error('Please enter a valid phone number (e.g. +91 98765 43210)');
          return;
        }
      }
      
      // E.164 regex check
      if (!/^\+[1-9]\d{1,14}$/.test(formattedPhone)) {
        toast.error('Invalid phone number format. Must start with + and country code.');
        return;
      }
    }

    setSaving(true);
    const loadingToast = toast.loading('Updating profile details...');

    try {
      // 1. Update user attributes in AWS Cognito
      await authService.updateUserAttributes({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: formattedPhone,
        address: address.trim(),
      });

      // 2. Update user profile in MySQL backend API
      const token = await authService.getAccessToken();
      const response = await fetch(`/api/users/${user.id}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phoneNumber: formattedPhone,
          address: address.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to synchronize profile changes with database.');
      }

      toast.success('Profile updated successfully!', { id: loadingToast });
      
      // Reload page to re-initialize auth state and pull fresh data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'An error occurred while updating profile.', { id: loadingToast });
    } finally {
      setSaving(false);
    }
  };

  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'ST';
  const board = user.attributes?.board || user.attributes?.['custom:board'] || 'Not Specified';
  const standard = user.attributes?.standard || user.attributes?.['custom:standard'] || 'Not Enrolled';
  const medium = user.attributes?.medium || user.attributes?.['custom:medium'] || 'English';
  const subscriptionStatus = user.attributes?.subscriptionStatus || user.attributes?.['custom:subscriptionStatus'] || 'Trial';
  const trialEndDate = user.attributes?.trialEndDate || user.attributes?.['custom:trialEndDate'] || '';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8 animate-fadeIn text-slate-800">
      {/* Top Banner Cover */}
      <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-700 h-40 shadow-xl shadow-purple-500/10">
        <div className="absolute inset-0 bg-black/10 backdrop-blur-sm"></div>
        <div className="absolute top-4 right-4 text-white/30">
          <FiSettings size={48} className="animate-spin-slow" style={{ animationDuration: '20s' }} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 -mt-20 relative z-10">
        {/* Left Card: Quick Bio & Badges */}
        <div className="lg:col-span-1 bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-gray-200/50 flex flex-col items-center text-center space-y-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-purple-600 to-indigo-600 text-white font-extrabold text-2xl flex items-center justify-center shadow-lg ring-4 ring-white">
              {initials}
            </div>
            <span className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-green-500 border-2 ring-2 ring-white flex items-center justify-center text-[10px] text-white">✓</span>
          </div>

          <div className="space-y-1">
            <h2 className="text-xl font-bold text-gray-900 leading-tight">
              {firstName} {lastName}
            </h2>
            <p className="text-xs text-gray-500 font-semibold">{user.email}</p>
          </div>

          <div className="w-full border-t border-gray-100 pt-4 flex flex-col items-stretch gap-3">
            <div className="flex items-center justify-between text-xs font-semibold p-2.5 bg-gray-50 rounded-xl">
              <span className="text-gray-500">Subscription</span>
              <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                subscriptionStatus.toLowerCase() === 'active' 
                  ? 'bg-green-100 text-green-700 border border-green-200' 
                  : 'bg-amber-100 text-amber-700 border border-amber-200'
              }`}>
                {subscriptionStatus}
              </span>
            </div>

            {trialEndDate && subscriptionStatus.toLowerCase() !== 'active' && (
              <div className="flex items-center justify-between text-xs font-semibold p-2.5 bg-red-50/50 rounded-xl text-red-700 border border-red-100/50">
                <span className="flex items-center gap-1.5"><FiCalendar /> Trial Ends</span>
                <span>{new Date(trialEndDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Card: Form Details */}
        <form onSubmit={handleSave} className="lg:col-span-2 bg-white border border-gray-100 rounded-3xl p-8 shadow-xl shadow-gray-200/50 space-y-6">
          <div className="border-b border-gray-100 pb-4">
            <h3 className="text-lg font-bold text-gray-900">Personal Information</h3>
            <p className="text-xs text-gray-500 font-semibold mt-1">Manage and edit your student registration profile details.</p>
          </div>

          {/* Form Fields Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5"><FiUser className="text-purple-600" /> First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-800 font-medium focus:outline-none focus:bg-white focus:border-purple-500/50 focus:ring-4 focus:ring-purple-500/5 transition duration-150"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5"><FiUser className="text-purple-600" /> Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-800 font-medium focus:outline-none focus:bg-white focus:border-purple-500/50 focus:ring-4 focus:ring-purple-500/5 transition duration-150"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5"><FiPhone className="text-purple-600" /> Phone Number</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter phone number"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-800 font-medium focus:outline-none focus:bg-white focus:border-purple-500/50 focus:ring-4 focus:ring-purple-500/5 transition duration-150"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5"><FiMail className="text-gray-400" /> Email Address (Read-only)</label>
              <div className="relative">
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="w-full bg-gray-100/80 border border-gray-200 text-gray-400 rounded-xl p-3 pl-3 pr-10 text-xs font-semibold cursor-not-allowed"
                />
                <FiLock className="absolute right-3 top-3.5 text-gray-400" size={14} />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5"><FiMapPin className="text-purple-600" /> Home Address</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter your complete address"
              rows={2}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-800 font-medium focus:outline-none focus:bg-white focus:border-purple-500/50 focus:ring-4 focus:ring-purple-500/5 transition duration-150 resize-none"
            />
          </div>

          <div className="border-t border-gray-100 pt-6 space-y-4">
            <h4 className="text-xs font-extrabold uppercase tracking-wider text-gray-400">Enrolled Curriculum Info</h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl space-y-1">
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider flex items-center gap-1"><FiBookOpen /> Standard</span>
                <p className="text-xs font-bold text-slate-700 flex items-center justify-between">
                  <span>{standard}</span>
                  <FiLock className="text-slate-400" size={10} />
                </p>
              </div>

              <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl space-y-1">
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider flex items-center gap-1"><FiGlobe /> Medium</span>
                <p className="text-xs font-bold text-slate-700 flex items-center justify-between">
                  <span>{medium}</span>
                  <FiLock className="text-slate-400" size={10} />
                </p>
              </div>

              <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl space-y-1">
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider flex items-center gap-1"><FiGlobe /> Curriculum Board</span>
                <p className="text-xs font-bold text-slate-700 flex items-center justify-between">
                  <span>{board}</span>
                  <FiLock className="text-slate-400" size={10} />
                </p>
              </div>
            </div>
          </div>

          {/* Submit Action */}
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 text-white rounded-xl text-xs font-bold shadow-lg shadow-purple-500/20 hover:shadow-purple-700/30 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
              ) : (
                <FiSave size={14} />
              )}
              <span>Save Profile Changes</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;